import { useEffect, useRef, useState } from 'react'

/**
 * Tracks whether a sticky element is pinned, by watching a zero-height
 * sentinel rendered right above it. The sentinel leaves the viewport through
 * the top edge (past `topOffset`) exactly when the element starts sticking.
 */
export function useIsStuck<T extends Element>(topOffset: number) {
  const sentinelRef = useRef<T>(null)
  const [stuck, setStuck] = useState(false)

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return
        setStuck(
          !entry.isIntersecting && entry.boundingClientRect.top <= topOffset
        )
      },
      { rootMargin: `-${topOffset + 1}px 0px 0px 0px`, threshold: 0 }
    )
    observer.observe(sentinel)

    return () => observer.disconnect()
  }, [topOffset])

  return { sentinelRef, stuck }
}
